import { SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Path, useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../schema'
import { getLinkableContactObjectName, IntegrationServiceId, SALESFORCE_BASED_INTEGRATIONS } from '../../../../../../common/integrations'
import { useCallback, useMemo } from 'react'
import {
  DescriptorType,
  DetailedCRMAccountIntegrationMapping,
  FilterOperator,
  GroupOperator,
  type RelationshipSource,
  type RelationshipTarget,
} from '../../../../../../models/Account'
import { ContactLinkRow } from './ContactLinkRow'
import { contactLinkNodeWithSelectedField, contactLinkNodeWithSelectedObject } from './contactLinkNodes'
import { FormFieldDefinition } from '../../hooks'

type Nullable<T> = { [K in keyof T]: T[K] | null }

type NullableContactLink = {
  source: Nullable<RelationshipSource>
  target: Nullable<RelationshipTarget>
}

export interface ContactLinkingRowsProps {
  serviceId: IntegrationServiceId
  mappingPath: string
  isDisabled?: boolean
  onChange?: () => void
  allowClearing?: boolean
}

export const ContactLinkingRows: React.FC<ContactLinkingRowsProps> = ({ serviceId, mappingPath, isDisabled = false, onChange, allowClearing = false }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()

  const contactObjectRaw = watch(`mappings.contact.objectName` as const)

  const contactObject = useMemo(() => getLinkableContactObjectName(serviceId, contactObjectRaw ?? null), [contactObjectRaw, serviceId])

  const currentMapping = watch(mappingPath as Path<IntegrationSettings>) as DetailedCRMAccountIntegrationMapping | undefined

  const targetObjectName = currentMapping?.objectName || null

  const contactLinkingObjects: NullableContactLink[] = currentMapping?.contactLinks as NullableContactLink[]

  const relatedToLink = useMemo(() => {
    return (
      contactLinkingObjects?.find((link) => link.source?.object === contactObject || link.source?.object === 'AccountContactRelation') || {
        source: { object: contactObject ?? null, field: null },
        target: { object: targetObjectName, field: null },
      }
    )
  }, [contactLinkingObjects, contactObject, targetObjectName])

  const linkingObjectLink = useMemo(
    () => contactLinkingObjects?.find((link) => link.source?.object === targetObjectName) || null,
    [contactLinkingObjects, targetObjectName],
  )

  const updateContactLinks = useCallback(
    (updatedLinks: NullableContactLink[]) => {
      setValue(`${mappingPath}.contactLinks` as Path<IntegrationSettings>, updatedLinks, { shouldDirty: true, shouldValidate: true })
    },
    [mappingPath, setValue],
  )

  const onTargetFieldCleared = useCallback(() => {
    const cleared: NullableContactLink = { source: relatedToLink.source, target: { ...relatedToLink.target, field: null } }
    updateContactLinks(cleared.source.field ? [cleared] : [])
    onChange?.()
  }, [relatedToLink, updateContactLinks, onChange])

  const onSourceFieldCleared = useCallback(() => {
    const cleared: NullableContactLink = { source: { ...relatedToLink.source, field: null }, target: relatedToLink.target }
    updateContactLinks(cleared.target.field ? [cleared] : [])
    onChange?.()
  }, [relatedToLink, updateContactLinks, onChange])

  const onRelatedToContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinkWithFilters: NullableContactLink =
        updatedLink.source.object === 'AccountContactRelation' && updatedLink.source.field
          ? {
              ...updatedLink,
              source: {
                ...updatedLink.source,
                filters: [
                  {
                    groupOperator: GroupOperator.AND,
                    expressions: [
                      {
                        field: 'ContactId',
                        operator: FilterOperator.EQ,
                        source: {
                          type: DescriptorType.VARIABLE,
                          value: 'contactId',
                        },
                      },
                    ],
                  },
                ],
              },
            }
          : updatedLink

      const updatedAdditionalLinks =
        updatedLinkWithFilters.target.object !== targetObjectName
          ? linkingObjectLink
            ? {
                ...linkingObjectLink,
                target: {
                  object: updatedLinkWithFilters.target.object,
                  field: linkingObjectLink.target.field,
                  valueType: linkingObjectLink.target.valueType,
                  valueFormat: linkingObjectLink.target.valueFormat,
                },
              }
            : {
                source: {
                  object: targetObjectName ?? null,
                  field: updatedLinkWithFilters.target.field,
                  valueType: updatedLinkWithFilters.target.valueType,
                  valueFormat: updatedLinkWithFilters.target.valueFormat,
                },
                target: { object: updatedLinkWithFilters.target.object, field: null },
              }
          : null

      const updatedLinks =
        updatedLink.target.object !== targetObjectName
          ? [updatedLinkWithFilters, ...(updatedAdditionalLinks ? [updatedAdditionalLinks] : [])]
          : [updatedLinkWithFilters]

      updateContactLinks(updatedLinks)
      onChange?.()
    },
    [linkingObjectLink, targetObjectName, updateContactLinks, onChange],
  )

  const onLinkingObjectContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinks = [relatedToLink, updatedLink]
      updateContactLinks(updatedLinks)
      onChange?.()
    },
    [relatedToLink, updateContactLinks, onChange],
  )

  const predefinedRelatedToSourceObjectOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = []
    if (contactObject) {
      options.push({
        id: contactObject,
        label: contactObject,
      })
    }
    if (SALESFORCE_BASED_INTEGRATIONS.includes(serviceId)) {
      options.push({
        id: 'AccountContactRelation',
        label: 'AccountContactRelation',
      })
    }
    return options
  }, [contactObject, serviceId])

  const onSourceObjectSelected = (opt: SelectOption) => {
    onRelatedToContactLinkChanged({ source: contactLinkNodeWithSelectedObject(relatedToLink.source, opt.id), target: relatedToLink.target })
  }

  const onSourceFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    onRelatedToContactLinkChanged({ source: contactLinkNodeWithSelectedField(relatedToLink.source.object, opt, fieldDef), target: relatedToLink.target })
  }

  const onTargetObjectSelected = (opt: SelectOption) => {
    onRelatedToContactLinkChanged({ source: relatedToLink.source, target: contactLinkNodeWithSelectedObject(relatedToLink.target, opt.id) })
  }

  const onTargetFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    onRelatedToContactLinkChanged({ source: relatedToLink.source, target: contactLinkNodeWithSelectedField(relatedToLink.target.object, opt, fieldDef) })
  }

  const onAdditionalSourceObjectSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    if (!linkingObjectLink) {
      return
    }
    onLinkingObjectContactLinkChanged({
      source: contactLinkNodeWithSelectedField(linkingObjectLink.source.object, opt, fieldDef),
      target: linkingObjectLink.target,
    })
  }

  const onAdditionalTargetFieldSelected = (opt: SelectOption, fieldDef: FormFieldDefinition | undefined) => {
    if (!linkingObjectLink) {
      return
    }
    onLinkingObjectContactLinkChanged({
      source: linkingObjectLink.source,
      target: contactLinkNodeWithSelectedField(linkingObjectLink.target.object, opt, fieldDef),
    })
  }

  return (
    <>
      <ContactLinkRow
        serviceId={serviceId}
        source={{
          objectName: relatedToLink?.source?.object || null,
          initialSelectedField: relatedToLink?.source?.field || null,
          predefinedObjectSelectOptions: predefinedRelatedToSourceObjectOptions,
          onObjectSelected: onSourceObjectSelected,
          onFieldSelected: onSourceFieldSelected,
          onFieldCleared: allowClearing ? onSourceFieldCleared : undefined,
          isFieldSelectionDisabled: isDisabled,
          isObjectSelectionDisabled: isDisabled,
        }}
        target={{
          objectName: relatedToLink?.target?.object || null,
          initialSelectedField: relatedToLink?.target?.field || null,
          onObjectSelected: onTargetObjectSelected,
          onFieldSelected: onTargetFieldSelected,
          onFieldCleared: allowClearing ? onTargetFieldCleared : undefined,
          isFieldSelectionDisabled: isDisabled,
          isObjectSelectionDisabled: isDisabled,
        }}
      />
      {linkingObjectLink ? (
        <ContactLinkRow
          serviceId={serviceId}
          source={{
            objectName: linkingObjectLink?.source?.object || null,
            initialSelectedField: linkingObjectLink?.source?.field || null,
            onObjectSelected: () => {},
            onFieldSelected: onAdditionalSourceObjectSelected,
            isObjectSelectionDisabled: true,
            isFieldSelectionDisabled: isDisabled,
          }}
          target={{
            objectName: linkingObjectLink?.target?.object || null,
            initialSelectedField: linkingObjectLink?.target?.field || null,
            onObjectSelected: () => {},
            onFieldSelected: onAdditionalTargetFieldSelected,
            isObjectSelectionDisabled: isDisabled,
            isFieldSelectionDisabled: isDisabled,
          }}
        />
      ) : null}
    </>
  )
}
